import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { groupBuyApi, homeBannerApi, productApi, categoryApi } from '../api';
import { GroupBuy, HomeBanner, Product, Category } from '../types';
import GroupBuyCard from '../components/groupbuy/GroupBuyCard';
import ProductCard from '../components/product/ProductCard';
import Loading from '../components/common/Loading';
import { toImageUrl } from '../utils/image';
import { localized } from '../utils/i18nField';
import { useTranslation } from 'react-i18next';
import { SHOW_HOME_PRODUCTS, SHOW_HOME_LIMITED_TIME } from '../config/features';
import { FEE_SUMMARY } from '../config/pricing';

const HomePage: React.FC = () => {
  const [allCollecting, setAllCollecting] = useState<GroupBuy[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCat, setActiveCat] = useState<number | null>(null);
  const [ltCat, setLtCat] = useState<number | null>(null);
  const [productCatIds, setProductCatIds] = useState<Set<number>>(new Set());
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [slide, setSlide] = useState(0);
  const [heroUrl, setHeroUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    homeBannerApi.get()
      .then(r => setBanners(r.data.data.filter(b => b.imageUrl)))
      .catch(() => {});
    categoryApi.getAll()
      .then(cat => setCategories(cat.data.data))
      .finally(() => setLoading(false));
    groupBuyApi.getList('COLLECTING', 0, null, 100)
      .then(r => setAllCollecting(r.data.data.content))
      .catch(() => {});
    productApi.getList({ page: 0, size: 200 })
      .then(r => setProductCatIds(new Set(r.data.data.content.map(p => p.categoryId))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    productApi.getList({ categoryId: activeCat ?? undefined, sortType: 'new', page: 0, size: 5 })
      .then(r => setProducts(r.data.data.content.slice(0, 5)))
      .catch(() => {});
  }, [activeCat]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => setSlide(s => (s + 1) % banners.length), 8000);
    return () => clearInterval(timer);
  }, [banners.length]);

  if (loading) return <Loading />;

  const flatCats: Category[] = [];
  const walkCats = (cats: Category[]) => cats.forEach(c => { flatCats.push(c); if (c.children) walkCats(c.children); });
  walkCats(categories);

  const activeCatObj = flatCats.find(c => c.id === activeCat);
  const activeCatName = activeCatObj ? localized(activeCatObj.name, activeCatObj.nameEn, i18n.language) : null;

  // Products: 현재 판매중인 상품이 있는 카테고리만(하위 포함). 전체 /products 페이지에는 영향 없음.
  const catHasProducts = (c: Category): boolean => productCatIds.has(c.id) || (c.children ?? []).some(catHasProducts);
  const productCategories = categories.filter(catHasProducts);

  // Limited-Time: 현재 판매중인 한정판매가 있는 카테고리만, Products와 독립적으로 필터
  const ltCategories = Array.from(new Set(allCollecting.filter(g => g.categoryId != null).map(g => g.categoryId as number)))
    .map(id => flatCats.find(c => c.id === id))
    .filter((c): c is Category => !!c);
  const ltShown = allCollecting.filter(g => ltCat == null || g.categoryId === ltCat).slice(0, 5);
  const ltCatObj = flatCats.find(c => c.id === ltCat);
  const ltCatName = ltCatObj ? localized(ltCatObj.name, ltCatObj.nameEn, i18n.language) : null;

  const catBar = (items: Category[], active: number | null, onSelect: (id: number | null) => void) => (
    <div style={styles.catBar}>
      <button style={{ ...styles.catChip, ...(active === null ? styles.catChipActive : {}) }} onClick={() => onSelect(null)}>All</button>
      {items.map(c => (
        <button key={c.id} style={{ ...styles.catChip, ...(active === c.id ? styles.catChipActive : {}) }} onClick={() => onSelect(c.id)}>
          {localized(c.name, c.nameEn, i18n.language)}
        </button>
      ))}
    </div>
  );

  const goQuote = (e: React.FormEvent) => {
    e.preventDefault();
    const url = heroUrl.trim();
    navigate(url ? `/purchase-requests?url=${encodeURIComponent(url)}` : '/purchase-requests');
  };

  const bannerSlider = banners.length > 0 && (
    <div style={styles.banner}>
      {banners.map((b, i) => (
        <Link
          key={b.id}
          to={b.categoryId ? `/products?categoryId=${b.categoryId}` : '/products'}
          style={{ ...styles.slide, opacity: i === slide ? 1 : 0, pointerEvents: i === slide ? 'auto' : 'none' }}
        >
          <img src={toImageUrl(b.imageUrl)} alt="" style={styles.bannerImg} />
        </Link>
      ))}
      {banners.length > 1 && (
        <div style={styles.dots}>
          {banners.map((b, i) => (
            <button
              key={b.id}
              aria-label={`배너 ${i + 1}`}
              style={{ ...styles.dot, ...(i === slide ? styles.dotActive : {}) }}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={styles.page}>
      {bannerSlider}

      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>Buy anything from Korea.</h1>
        <p style={styles.heroSub}>Paste a link — we buy it, pack it, and ship it to your door.</p>
        <form style={styles.heroForm} onSubmit={goQuote}>
          <input
            style={styles.heroInput}
            placeholder="https://... paste any Korean store link"
            value={heroUrl}
            onChange={e => setHeroUrl(e.target.value)}
            aria-label="Product URL"
          />
          <button type="submit" style={styles.heroBtn}>Get a quote</button>
        </form>
        <p style={styles.heroNote}>Service fee {FEE_SUMMARY} · No purchase until you approve the quote · Packing video included</p>
      </section>

      <section style={styles.howSection}>
        <h2 style={styles.sectionTitle}>How It Works</h2>
        <div style={styles.steps}>
          {[
            { icon: '🔗', title: 'Send a link', desc: 'Paste the link of any item sold in Korea — shops, brand sites, or marketplaces.' },
            { icon: '🧾', title: 'Get a quote', desc: 'We check availability and send you the item price, service fee, and shipping.' },
            { icon: '💳', title: 'Pay securely', desc: 'Approve the quote and pay with PayPal. Nothing is bought before you approve.' },
            { icon: '📦', title: 'We ship to you', desc: 'We buy it, film the packing, and ship from Korea with a tracking number.' },
          ].map(s => (
            <div key={s.title} style={styles.step}>
              <span style={styles.stepIcon}>{s.icon}</span>
              <h3 style={styles.stepTitle}>{s.title}</h3>
              <p style={styles.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
        <div style={styles.howCtaWrap}>
          <Link to="/purchase-requests" style={styles.howCta}>Request an item →</Link>
        </div>
      </section>

      {SHOW_HOME_PRODUCTS && (categories.length > 0 || products.length > 0) && (
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>Products{activeCatName ? ` · ${activeCatName}` : ''}</h2>
              <p style={styles.sectionSub}>Official webtoon goods.</p>
            </div>
            <Link to="/products" style={styles.seeAll}>See all →</Link>
          </div>
          {productCategories.length > 0 && catBar(productCategories, activeCat, setActiveCat)}
          <div style={styles.grid}>
            {products.length > 0
              ? products.map(p => <ProductCard key={p.id} product={p} />)
              : <p style={styles.prodEmpty}>No products in this category yet.</p>}
          </div>
        </section>
      )}

      {SHOW_HOME_LIMITED_TIME && allCollecting.length > 0 && (
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>Limited-Time{ltCatName ? ` · ${ltCatName}` : ''}</h2>
              <p style={styles.sectionSub}>Limited time — order before the deadline!</p>
            </div>
            <Link to="/group-buys" style={styles.seeAll}>See all →</Link>
          </div>
          {ltCategories.length > 0 && catBar(ltCategories, ltCat, setLtCat)}
          <div style={styles.grid}>
            {ltShown.map(gb => <GroupBuyCard key={gb.id} groupBuy={gb} />)}
          </div>
        </section>
      )}

      {allCollecting.length === 0 && products.length === 0 && categories.length === 0 && (
        <div style={styles.empty}>
          <p style={{ fontSize: 48, marginBottom: 16 }}>🛍️</p>
          <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Coming Soon!</p>
          <p style={{ color: '#999' }}>Our first drop is being prepared. Check back soon!</p>
        </div>
      )}

    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' },
  hero: { background: 'linear-gradient(135deg, #f6f0fd 0%, #fdf6f3 100%)', borderRadius: 20, padding: '64px 40px', margin: '0 0 60px', textAlign: 'center' },
  heroTitle: { fontSize: 38, fontWeight: 800, color: '#333', marginBottom: 12, lineHeight: 1.25 },
  heroSub: { fontSize: 16, color: '#777', marginBottom: 28 },
  heroForm: { display: 'flex', gap: 10, maxWidth: 620, margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center' },
  heroInput: { flex: '1 1 300px', padding: '15px 18px', border: '1px solid #e3d4f2', borderRadius: 10, fontSize: 15, outline: 'none', background: '#fff', boxSizing: 'border-box' },
  heroBtn: { padding: '15px 28px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  heroNote: { fontSize: 13, color: '#a99cbb', marginTop: 18 },
  banner: { position: 'relative', aspectRatio: '3 / 1', borderRadius: 20, overflow: 'hidden', margin: '24px 0 20px', background: '#f8f5fd' },
  slide: { position: 'absolute', inset: 0, display: 'block', transition: 'opacity 0.6s ease' },
  bannerImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  dots: { position: 'absolute', bottom: 14, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8, zIndex: 2 },
  dot: { width: 9, height: 9, borderRadius: '50%', border: 'none', padding: 0, background: 'rgba(255,255,255,0.55)', cursor: 'pointer' },
  dotActive: { background: '#fff', boxShadow: '0 0 0 2px rgba(0,0,0,0.15)' },
  section: { marginBottom: 60 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 },
  sectionTitle: { fontSize: 22, fontWeight: 800, color: '#333', marginBottom: 4 },
  sectionSub: { fontSize: 13, color: '#aaa' },
  seeAll: { fontSize: 13, color: '#b39ddb', fontWeight: 600 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 },
  catBar: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 },
  catChip: { padding: '8px 16px', border: '1px solid #eee', background: '#fff', borderRadius: 20, fontSize: 14, color: '#666', cursor: 'pointer', fontWeight: 500 },
  catChipActive: { background: '#ece3f7', color: '#b39ddb', fontWeight: 700, borderColor: '#d9c8ef' },
  prodEmpty: { gridColumn: '1 / -1', textAlign: 'center', color: '#bbb', fontSize: 14, padding: '50px 0' },
  empty: { textAlign: 'center', padding: '80px 0 60px' },
  howSection: { background: '#fafafa', borderRadius: 16, padding: '48px 40px', marginBottom: 60 },
  steps: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24, marginTop: 32 },
  howCtaWrap: { textAlign: 'center', marginTop: 32 },
  howCta: { display: 'inline-block', padding: '13px 30px', background: '#b39ddb', color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none' },
  step: { textAlign: 'center', padding: '0 8px' },
  stepIcon: { fontSize: 36, display: 'block', marginBottom: 12 },
  stepTitle: { fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 8 },
  stepDesc: { fontSize: 13, color: '#888', lineHeight: 1.6 },
};

export default HomePage;
